import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SectionHeading } from "@/shared/ui";

import { DeploymentAreaCard } from "@/features/deployment-settings/components/deployment-area-card";
import type { DeploymentAreaCapacity } from "@/features/deployment-settings/model/admin-settings";

interface DeploymentAreasSectionProps {
  areas: DeploymentAreaCapacity[];

  canEdit: boolean;
  isEditing: boolean;
  isSaving: boolean;
  hasChanges: boolean;
  validationError: string | null;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onRequestSave: () => void;
  onChangeCapacity: (areaId: string, value: number | "") => void;
}

export function DeploymentAreasSection({
  areas,

  canEdit,
  isEditing,
  isSaving,
  hasChanges,
  validationError,
  onStartEditing,
  onCancelEditing,
  onRequestSave,
  onChangeCapacity,
}: DeploymentAreasSectionProps) {
  return (
    <Stack id="deployment-areas" spacing={2.5}>
      <Stack spacing={0.75}>
        <SectionHeading
          component="h2"
          icon={<LocationOnOutlinedIcon />}
          action={
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              {isEditing ? (
                <>
                  <Button variant="text" onClick={onCancelEditing} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveOutlinedIcon />}
                    onClick={onRequestSave}
                    disabled={validationError != null || !hasChanges || isSaving}
                  >
                    Review changes
                  </Button>
                </>
              ) : canEdit ? (
                <Button
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  onClick={onStartEditing}
                >
                  Edit
                </Button>
              ) : null}
            </Stack>
          }
        >
          Areas
        </SectionHeading>
        <Typography variant="body2" color="text.secondary">
          Set an optional capacity for each area. Population thresholds using percentage of area
          capacity need a capacity value to resolve correctly.
        </Typography>
        {validationError != null && isEditing && (
          <Alert severity="error" role="alert">
            {validationError}
          </Alert>
        )}
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {areas.map((area) => (
          <DeploymentAreaCard
            key={area.id}
            area={area}
            isEditing={isEditing}
            onChange={(value) => onChangeCapacity(area.id, value)}
          />
        ))}
      </Box>
    </Stack>
  );
}
